import React, { useState, useRef, useEffect, useContext } from "react";
import authFetch from "../../authFetch";
import PlatformCtx from "../../state/PlatformCtx";
import Modal from "../../shared/Modal";
import useBnavFab from "../../shared/useBnavFab";
import { getEffectiveTaxonomy } from "../../permissions/checks";

const CUISINE_DB={
  breakfast:[
    {name:"Shakshuka",nameAr:"شكشوكة"},{name:"Ful Medames",nameAr:"فول مدمس"},
    {name:"Labneh & Za'atar",nameAr:"لبنة وزعتر"},{name:"Msabbaha",nameAr:"مسبحة"},
    {name:"Balaleet",nameAr:"بلاليط"},{name:"Fatayer Jibn",nameAr:"فطاير جبن"},
    {name:"Tamees",nameAr:"تميس"},{name:"Manakeesh",nameAr:"مناقيش"},
    {name:"Masoob",nameAr:"مصوب"},{name:"Hummus & Bread",nameAr:"حمص وخبز"},
    {name:"Aseeda",nameAr:"عصيدة"},{name:"Bayd bil Laham",nameAr:"بيض باللحم"},
    {name:"Kaak bil Simsim",nameAr:"كعك بالسمسم"},{name:"Fool bil Bayd",nameAr:"فول بالبيض"},
    {name:"Cheese & Honey Platter",nameAr:"طبق جبن وعسل"},{name:"Harees Breakfast",nameAr:"هريس الفطور"},
    {name:"Muhammar Rice",nameAr:"محمر"},{name:"Lentil Soup",nameAr:"شوربة عدس"},
    {name:"Za'atar Flatbread",nameAr:"خبز الزعتر"},{name:"Saffron Oats",nameAr:"شوفان بالزعفران"},
  ],
  lunch:[
    {name:"Kabsa",nameAr:"كبسة"},{name:"Jareesh",nameAr:"جريش"},
    {name:"Margoog",nameAr:"مرقوق"},{name:"Mandi",nameAr:"مندي"},
    {name:"Madfoon",nameAr:"مدفون"},{name:"Saleeg",nameAr:"سليق"},
    {name:"Lamb Ouzi",nameAr:"أوزي"},{name:"Grilled Hammour",nameAr:"هامور مشوي"},
    {name:"Shawarma Platter",nameAr:"شاورما"},{name:"Chicken Machboos",nameAr:"مجبوس دجاج"},
    {name:"Mulukhiyah",nameAr:"ملوخية"},{name:"Mixed Grill",nameAr:"مشاوي مشكلة"},
    {name:"Stuffed Grape Leaves",nameAr:"ورق عنب"},{name:"Kofta",nameAr:"كفتة"},
    {name:"Sayadieh",nameAr:"صيادية"},{name:"Musakhan",nameAr:"مسخن"},
    {name:"Fattoush Platter",nameAr:"طبق فتوش"},{name:"Lebanese Kibbeh",nameAr:"كبة لبنانية"},
    {name:"Lamb Chops & Rice",nameAr:"ضلوع مع رز"},{name:"Freekeh bil Dajaj",nameAr:"فريكة بالدجاج"},
  ],
  dinner:[
    {name:"Harees",nameAr:"هريس"},{name:"Thareed",nameAr:"ثريد"},
    {name:"Chicken Tikka",nameAr:"تكا دجاج"},{name:"Shrimp Pasta",nameAr:"باستا الجمبري"},
    {name:"Lamb Stew",nameAr:"يخنة لحم"},{name:"Grilled Sea Bass",nameAr:"قاروص مشوي"},
    {name:"Beef Tenderloin",nameAr:"فيليه لحم"},{name:"Mansaf",nameAr:"منسف"},
    {name:"Stuffed Zucchini",nameAr:"كوسا محشي"},{name:"Shish Tawook",nameAr:"شيش طاووق"},
    {name:"Fish Sayadieh",nameAr:"سمك صيادية"},{name:"Pasta Arrabiata",nameAr:"باستا أرابياتا"},
    {name:"Cream Lentil Soup",nameAr:"شوربة عدس كريمية"},{name:"Musakhan Rolls",nameAr:"لفائف مسخن"},
    {name:"Grilled Salmon",nameAr:"سلمون مشوي"},{name:"Lamb Ouzi",nameAr:"أوزي"},
    {name:"Chicken Shawarma Bowl",nameAr:"وجبة شاورما دجاج"},{name:"Kousa Mahshi",nameAr:"محشي"},
    {name:"Bamia bil Laham",nameAr:"بامية باللحم"},{name:"Dajaj Mashwi",nameAr:"دجاج مشوي"},
  ],
};

/* ── Default preference list (Super Admin can extend/replace via platformMenuConfig) ── */
const DEFAULT_MENU_PREFERENCES=[
  "Saudi","Lebanese","Mediterranean","Yemeni","Egyptian","Moroccan",
  "Asian","Indian","Turkish","Italian","Mexican","Western",
  "Healthy","High Protein","Low Carb","Low Calorie","Vegetarian","Vegan","Gluten-Free",
  "Light","Hearty","Quick","Family Friendly","Spicy","Mild","Grilled","Baked","Fried",
];

const DIETARY_SUGGESTIONS=[
  "Nut allergy","Peanut allergy","Tree nut allergy","Sesame allergy","Shellfish allergy","Fish allergy",
  "Egg allergy","Soy allergy","Wheat allergy",
  "Gluten-free","Celiac disease",
  "Dairy-free","Lactose intolerant",
  "Vegetarian","Vegan",
  "Diabetic — low sugar","Low sodium","Low cholesterol","Low carb",
  "Low FODMAP",
];

/* ── Course structure defaults ── */
const DEFAULT_COURSE_SCALING=[{max:2,opts:1},{max:4,opts:2},{max:9,opts:3},{max:99,opts:4}];
const DEFAULT_COURSES_PER_MEAL={
  breakfast:["Main"],
  lunch:["Appetizer","Main","Dessert"],
  dinner:["Appetizer","Soup","Main","Side","Dessert"]
};

/* ── Menu Carousel — iOS-style swipeable day cards for finalized weekly menu ── */
function MenuCarousel({weekDays,MEAL_TYPES,DAY_LABELS,getFinalMeal,isRTL,now,fmt,initDay,t}){
  const [sel,setSel]=useState(initDay);
  const scrollRef=useRef(null);
  const scrollTimer=useRef(null);
  const CARD_W=260;
  const GAP=12;

  const scrollTo=(idx,smooth)=>{
    if(!scrollRef.current)return;
    /* Spacer div already centers card 0. Each subsequent card is CARD_W+GAP further. */
    const target=idx*(CARD_W+GAP);
    scrollRef.current.scrollTo({left:target,behavior:smooth?"smooth":"auto"});
  };

  useEffect(()=>{scrollTo(sel,true);},[sel]);
  useEffect(()=>{setTimeout(()=>scrollTo(initDay,false),50);},[]);

  const onScroll=()=>{
    if(scrollTimer.current)clearTimeout(scrollTimer.current);
    scrollTimer.current=setTimeout(()=>{
      if(!scrollRef.current)return;
      const idx=Math.round(scrollRef.current.scrollLeft/(CARD_W+GAP));
      const clamped=Math.max(0,Math.min(idx,weekDays.length-1));
      if(clamped!==sel)setSel(clamped);
    },60);
  };

  return(
    <div>
      <div style={{display:"flex",justifyContent:"center",alignItems:"center",gap:5,paddingBottom:6}}>
        {weekDays.map((_,i)=>(
          <button key={i} onClick={()=>setSel(i)} style={{height:6,borderRadius:3,border:"none",cursor:"pointer",transition:"all .2s",padding:0,
            width:sel===i?22:6,background:sel===i?"var(--ink)":"var(--g200)"}}/>
        ))}
      </div>
      <div style={{position:"relative"}}>
        {/* Top/bottom gradient fade so carousel blends with page */}
        <div style={{position:"absolute",top:0,left:0,right:0,height:16,background:"linear-gradient(to bottom, var(--page,#f5f4f2), transparent)",zIndex:1,pointerEvents:"none"}}/>
        <div style={{position:"absolute",bottom:0,left:0,right:0,height:16,background:"linear-gradient(to top, var(--page,#f5f4f2), transparent)",zIndex:1,pointerEvents:"none"}}/>
      <div ref={scrollRef} onScroll={onScroll} style={{display:"flex",gap:GAP,overflowX:"auto",WebkitOverflowScrolling:"touch",scrollbarWidth:"none",msOverflowStyle:"none",paddingBottom:16,paddingTop:12,background:"var(--g50)"}}>
        {/* Left spacer */}
        <div style={{flexShrink:0,width:`calc(50% - ${CARD_W/2}px)`}}/>
        {weekDays.map((d,i)=>{
          const isToday=fmt(d)===fmt(now);
          const isSel=sel===i;
          const dayLabel=d.toLocaleDateString(isRTL?"ar-SA":"en-GB",{weekday:"short"});
          const dateLabel=d.toLocaleDateString(isRTL?"ar-SA":"en-GB",{month:"short",day:"numeric"});
          return(
            <div key={i} onClick={()=>setSel(i)} style={{flexShrink:0,width:CARD_W}}>
              <div style={{
                background:isSel?"rgba(255,255,255,.85)":"rgba(255,255,255,.4)",
                backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",
                border:isSel?"1px solid rgba(255,255,255,.7)":"1px solid rgba(0,0,0,.04)",
                borderRadius:16,overflow:"hidden",
                boxShadow:isSel?"0 12px 40px rgba(0,0,0,.12)":"none",
                transform:isSel?"scale(1)":"scale(0.92)",
                opacity:isSel?1:0.4,
                transition:"transform .3s, opacity .3s, box-shadow .3s, background .3s, border .3s",
                display:"flex",flexDirection:"column"}}>
                <div style={{padding:"12px 16px 6px",display:"flex",alignItems:"center",justifyContent:"space-between"}}>
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    <div style={{fontSize:17,fontWeight:700,color:"var(--ink)"}}>{dayLabel}</div>
                    {isToday&&<span style={{padding:"1px 7px",borderRadius:8,background:"var(--ink)",color:"white",fontSize:8,fontWeight:700,textTransform:"uppercase"}}>{t.today}</span>}
                  </div>
                  <div style={{fontSize:12,color:"var(--g400)"}}>{dateLabel}</div>
                </div>
                <div style={{flex:1}}>
                  {MEAL_TYPES.map((mt,mti)=>{
                    const fm=getFinalMeal(d,mt);
                    return(
                      <div key={mt}>
                        <div style={{padding:"8px 16px 2px",fontSize:12,fontWeight:700,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".06em"}}>{mt}</div>
                        {fm?.courses?fm.courses.map((course,ci)=>(
                          <div key={ci}>
                            {fm.courses.length>1&&<div style={{padding:"1px 16px 0",fontSize:8,fontWeight:600,color:"var(--g300)",textTransform:"uppercase"}}>{course.label}</div>}
                            {(course.options||[]).map((opt,oi)=>(
                              <div key={oi} style={{padding:"5px 16px",borderBottom:"1px solid rgba(0,0,0,.03)"}}>
                                <div style={{fontSize:13,fontWeight:600,color:"var(--ink)",lineHeight:1.25,marginBottom:1}}>{isRTL&&opt.nameAr?opt.nameAr:opt.name}{opt.tags&&opt.tags.length>0&&<span style={{marginInlineStart:6}}>{opt.tags.slice(0,2).map((tg,ti)=><span key={ti} style={{fontSize:7,padding:"1px 4px",borderRadius:5,background:"var(--g50)",color:"var(--g400)",border:"1px solid var(--g100)",marginInlineEnd:2,whiteSpace:"nowrap",verticalAlign:"middle"}}>{tg}</span>)}</span>}</div>
                                {opt.description&&<div style={{fontSize:11,color:"var(--g400)",lineHeight:1.3}}>{opt.description}</div>}
                              </div>
                            ))}
                          </div>
                        )):(
                          <div style={{padding:"6px 16px",fontSize:11,color:"var(--g300)"}}>—</div>
                        )}
                        {mti<MEAL_TYPES.length-1&&<div style={{height:1,background:"var(--g100)",margin:"0 16px"}}/>}
                      </div>
                    );
                  })}
                </div>
                <div style={{padding:"6px 16px",borderTop:"1px solid rgba(0,0,0,.03)",textAlign:"center"}}>
                  <div style={{fontSize:9,color:"var(--g300)"}}>{isRTL?"اسحب لمزيد من الأيام":"Swipe for more days"}</div>
                </div>
              </div>
            </div>
          );
        })}
        <div style={{flexShrink:0,width:`calc(50% - ${CARD_W/2}px)`}}/>
      </div>
      </div>
    </div>
  );
}

function MenuModule({state,dispatch,t,wsKey,currentUser}){
  const platform=useContext(PlatformCtx);
  const {
    meals=[],mealPreferences=[],menuCycles=[],
    menuGeneratedOptions=[],menuVotes=[],
    mealCategories=[],users=[],lang,
    menuDietaryRequirements=[],menuPermissions={},
    menuPreferences:wsPreferences=[],
    menuDeadlineOverride={},
    menuVotingModeOverride,
    householdData={},
    menuOptionsPerCourseOverride,
    menuCoursesOverride,
  }=state;
  const isRTL=lang==="ar";
  const platformMenuCfg=platform?.platformMenuConfig||{};
  const platformProfiles=platform?.platformUserProfiles||[];
  const availablePreferences=wsPreferences.length>0?wsPreferences:(platformMenuCfg.preferences||DEFAULT_MENU_PREFERENCES);
  /* Build defaults from profile IDs — family group + cook get addDishes/vote; owner gets finalize */
  const familyIds=platformProfiles.filter(p=>p.group==="Family"||p.id==="owner").map(p=>p.id);
  const addMealIds=[...familyIds,...platformProfiles.filter(p=>p.id.includes("cook")).map(p=>p.id)];
  const DEFAULT_PERMISSIONS={
    addDishes:addMealIds.length?addMealIds:["owner","family_manager","family_member","staff_cook"],
    generateMeals:familyIds.length?familyIds:["owner","family_manager","family_member"],
    vote:familyIds.length?familyIds:["owner","family_manager","family_member"],
    manageDietary:familyIds.length?familyIds:["owner","family_manager","family_member"],
    finalize:["owner"]
  };
  const effectivePerms={...DEFAULT_PERMISSIONS,...(platformMenuCfg.defaultPermissions||{}),...menuPermissions};
  /* canDo: check profile ID directly; "owner" always passes */
  const canDo=(action)=>{
    if(currentUser?.role==="owner")return true;
    const roles=effectivePerms[action]||[];
    return roles.includes(currentUser?.role);
  };

  const [tab,setTab]=useState("menu");
  useEffect(()=>{window.dispatchEvent(new CustomEvent("menu:tab",{detail:tab}));return()=>window.dispatchEvent(new CustomEvent("menu:tab",{detail:null}));},[tab]);
  const [weekOffset,setWeekOffset]=useState(0);
  const [selectedDay,setSelectedDay]=useState(()=>new Date().getDay());
  useEffect(()=>{setSelectedDay(weekOffset===0?new Date().getDay():0);},[weekOffset]);
  const [showGenerate,setShowGenerate]=useState(false);
  const [genScope,setGenScope]=useState("permanent");
  const [genMealTypes,setGenMealTypes]=useState(["lunch"]);
  const [genPrefs,setGenPrefs]=useState([]);
  const [genPrefSearch,setGenPrefSearch]=useState("");
  const [genCount,setGenCount]=useState(5);
  const [genLoading,setGenLoading]=useState(false);
  const [genError,setGenError]=useState(null);
  const [genOptionsPerCourse,setGenOptionsPerCourse]=useState(3);
  const [genOptionsCustom,setGenOptionsCustom]=useState("");
  const [showDietaryForm,setShowDietaryForm]=useState(false);
  const [selectedDietary,setSelectedDietary]=useState([]);
  const [customDietaryInput,setCustomDietaryInput]=useState("");

  const [mealSearch,setMealSearch]=useState("");
  const [mealCatFilter,setMealCatFilter]=useState("all");
  const [confirmDeleteMeal,setConfirmDeleteMeal]=useState(null);
  const [confirmDeleteDietary,setConfirmDeleteDietary]=useState(null);
  const [showAddMeal,setShowAddMeal]=useState(false);
  useBnavFab(({action})=>{if(action==="menu_generate"&&canDo("generateMeals"))setShowGenerate(true);else if(action==="menu_add"&&canDo("addDishes"))setShowAddMeal(true);else if(action==="add"&&tab==="dietary"&&canDo("manageDietary"))setShowDietaryForm(true);});
  const [votingMealTab,setVotingMealTab]=useState("breakfast");

  // ── Week helpers ─────────────────────────────────────────
  const now=new Date();
  const getWeekStart=(offset)=>{const d=new Date(now);const day=d.getDay();const diff=d.getDate()-day+(offset*7);d.setDate(diff);d.setHours(0,0,0,0);return d;};
  const weekStart=getWeekStart(weekOffset);
  const weekDays=Array.from({length:7},(_,i)=>{const d=new Date(weekStart);d.setDate(weekStart.getDate()+i);return d;});
  const fmt=(d)=>d.toISOString().slice(0,10);
  const MEAL_TYPES=["breakfast","lunch","dinner"];
  const DAY_LABELS=["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  // ── Cycle helpers ────────────────────────────────────────
  const currentWeekStart=fmt(getWeekStart(0));
  const isPastWeek=fmt(weekDays[0])<currentWeekStart;
  const cycle=(menuCycles||[]).find(c=>c.weekStart===fmt(weekDays[0])&&!c.deletedAt)||null;
  const isFinalized=cycle?.status==="finalized";
  const isVoting=!!(cycle&&!isFinalized);
  /* Voting mode: workspace override takes priority (allows switching mode even mid-cycle),
     then cycle's stored mode, then platform default */
  const effectiveVotingMode=menuVotingModeOverride||cycle?.data?.votingMode||platformMenuCfg.votingMode||"daily";
  const isWeeklyMode=effectiveVotingMode==="weekly";

  // ── Household size & course config ──────────────────────────
  const residents=householdData?.residents||4;
  const courseScaling=platformMenuCfg.courseScaling||DEFAULT_COURSE_SCALING;
  const getOptionsPerCourse=(r)=>{
    if(menuOptionsPerCourseOverride)return menuOptionsPerCourseOverride;
    for(const s of courseScaling){if(r<=s.max)return s.opts;}
    return 4;
  };
  const optionsPerCourse=getOptionsPerCourse(residents);
  const getEffectiveCourses=(mealType)=>{
    const src=menuCoursesOverride||platformMenuCfg.defaultCourses||DEFAULT_COURSES_PER_MEAL;
    return src[mealType]||(DEFAULT_COURSES_PER_MEAL[mealType]||["Main"]);
  };
  /* All known course labels for a meal type: configured defaults + any seen in the meals library */
  const getKnownCourseLabels=(mealType)=>{
    const set=new Set(getEffectiveCourses(mealType));
    (meals||[]).forEach(m=>{
      if(m.category===mealType&&!m.deletedAt&&m.data?.courses){
        m.data.courses.forEach(c=>{if(c.label)set.add(c.label);});
      }
    });
    return[...set];
  };

  // ── Vote / meal helpers ───────────────────────────────────
  /* Returns course-structured proposal { courses:[...] } or flat array for a slot.
     Primary source: meals library (course-structured meals scoped to the displayed week).
     Falls back to flat named meals in library, then legacy menuGeneratedOptions. */
  const getVotingMeals=(day,mealType)=>{
    const thisWeekStr=fmt(weekDays[0]);
    const thisWeekMs=new Date(thisWeekStr+"T12:00:00Z").getTime();
    const scopedMeals=(meals||[]).filter(m=>{
      if(m.deletedAt)return false;
      if(m.category!==mealType)return false;
      if(m.scope==="week"){
        /* Week-scoped: only include if it belongs to the displayed week (within 3-day tolerance) */
        const ws=(m.weekStart||"").slice(0,10);
        if(!ws)return false;
        const diffDays=Math.abs(new Date(ws+"T12:00:00Z").getTime()-thisWeekMs)/(86400000);
        if(diffDays>3)return false;
      }
      /* permanent (or no scope): always include */
      return true;
    });
    /* Merge options from all matching structured meals additively per course label.
       Permanent meals contribute their options every week.
       Week-scoped meals add their options only on their specific week.
       When both exist, all options are pooled together for voting. */
    const structured=scopedMeals.filter(m=>m.data?.courses?.length>0);
    if(structured.length>0){
      const courseMap={};
      structured.forEach(m=>{
        (m.data.courses||[]).forEach(course=>{
          const key=course.label||"Main";
          if(!courseMap[key])courseMap[key]={label:course.label,options:[]};
          courseMap[key].options.push(...(course.options||[]));
        });
      });
      const mergedCourses=Object.values(courseMap);
      if(mergedCourses.length>0)return{courses:mergedCourses};
    }
    /* Fallback: flat named meals → legacy display */
    if(scopedMeals.length>0)
      return scopedMeals.map(m=>({id:m.id,name:m.name,nameAr:m.nameAr||"",description:"",tags:m.tags||[]}));
    /* Last resort: legacy menuGeneratedOptions for old cycles */
    const optRow=(menuGeneratedOptions||[]).find(o=>o.cycleId===cycle?.id&&o.day===fmt(day)&&o.mealType===mealType&&!o.deletedAt);
    if(optRow?.dishes?.courses)return optRow.dishes;
    if(optRow?.dishes?.length)return optRow.dishes;
    return null;
  };
  /* Get the course-structured meal proposal for a slot.
     Returns { courses: [{label, options:[{name,nameAr,description,tags}]}] } or null. */
  const getMealProposal=(day,mealType)=>{
    const raw=getVotingMeals(day,mealType);
    if(!raw)return null;
    if(raw.courses)return raw; /* already course-structured */
    if(Array.isArray(raw)&&raw.length>0){
      /* Legacy meal array → wrap as single Main course */
      return{courses:[{label:"Main",options:raw}]};
    }
    return null;
  };
  /* IDs of all cycles whose weekStart matches the displayed week (handles cycle=null edge case) */
  const weekCycleIds=new Set((menuCycles||[]).filter(c=>c.weekStart===fmt(weekDays[0])&&!c.deletedAt).map(c=>c.id));
  const matchesCycle=(v)=>cycle?.id?v.cycleId===cycle.id:weekCycleIds.has(v.cycleId);

  /* ── Proposition voting helpers (approve / flag model) ────── */
  const getUserVote=(day,mealType)=>{
    const v=(menuVotes||[]).find(v=>matchesCycle(v)&&v.day===fmt(day)&&v.mealType===mealType
      &&v.dishOptionId==="proposition"&&v.userId===currentUser?.id&&!v.deletedAt);
    return v?.dishName||null; /* "approve" or "flag" or null */
  };
  const getVoteStats=(day,mealType)=>{
    const votes=(menuVotes||[]).filter(v=>matchesCycle(v)&&v.day===fmt(day)&&v.mealType===mealType
      &&v.dishOptionId==="proposition"&&!v.deletedAt);
    return{approve:votes.filter(v=>v.dishName==="approve").length,flag:votes.filter(v=>v.dishName==="flag").length,total:votes.length};
  };
  /* ── Per-option 1–5 rating voting ────────────────────────
     dishOptionId: "course_{ci}_opt_{oi}"
     dishName: "1"|"2"|"3"|"4"|"5"  (user's rating for that option) */
  const castOptionRating=(day,mealType,courseIndex,optionIndex,rating)=>{
    if(isFinalized||deadlinePassed||!canDo("vote"))return;
    const activeCycle=ensureCycle();
    const dayStr=isWeeklyMode?fmt(weekDays[0]):fmt(day);
    dispatch({type:"CAST_MENU_VOTE",vote:{
      id:`mv_${Date.now()}`,wsKey,cycleId:activeCycle.id,
      day:dayStr,mealType,dishOptionId:`course_${courseIndex}_opt_${optionIndex}`,
      dishName:`${rating}`,userId:currentUser?.id
    }});
    completeMenuVoteTask(activeCycle.id,currentUser?.id);
  };
  const getUserOptionRating=(day,mealType,courseIndex,optionIndex)=>{
    const dayStr=isWeeklyMode?fmt(weekDays[0]):fmt(day);
    const v=(menuVotes||[]).find(v=>matchesCycle(v)&&v.day===dayStr&&v.mealType===mealType
      &&v.dishOptionId===`course_${courseIndex}_opt_${optionIndex}`&&v.userId===currentUser?.id&&!v.deletedAt);
    return v!=null?parseInt(v.dishName):0;
  };
  /* Returns {optionIndex: totalScore} — sum of all users' ratings for each option */
  const getOptionTotalScores=(day,mealType,courseIndex,optionCount)=>{
    const dayStr=isWeeklyMode?fmt(weekDays[0]):fmt(day);
    const scores={};
    for(let oi=0;oi<optionCount;oi++){
      const votes=(menuVotes||[]).filter(v=>matchesCycle(v)&&v.day===dayStr&&v.mealType===mealType
        &&v.dishOptionId===`course_${courseIndex}_opt_${oi}`&&!v.deletedAt);
      scores[oi]=votes.reduce((sum,v)=>sum+(parseInt(v.dishName)||0),0);
    }
    return scores;
  };
  /* Legacy per-meal rating helpers kept for backward compat with old cycles */
  const getLegacyMealRating=(day,mealType,mealId)=>{
    const v=(menuVotes||[]).find(v=>matchesCycle(v)&&v.day===fmt(day)&&v.mealType===mealType&&v.dishOptionId===mealId&&v.userId===currentUser?.id&&!v.deletedAt);
    if(!v)return 0;
    return parseInt((v.dishName||"").split("|||")[1])||1;
  };
  const getLegacyTotalMealRating=(day,mealType,mealId)=>(menuVotes||[])
    .filter(v=>matchesCycle(v)&&v.day===fmt(day)&&v.mealType===mealType&&v.dishOptionId===mealId&&!v.deletedAt)
    .reduce((s,v)=>s+(parseInt((v.dishName||"").split("|||")[1])||1),0);

  /* ── Effective final menu ──────────────────────────────────
     Final menu now stores course structure per slot:
     { "2026-03-29": { lunch: { courses: [{label, options:[...]}] } } }
     Falls back to legacy { name, nameAr, tags } format for old cycles. */
  const effectiveFinalMenu=(()=>{
    if(!isFinalized||!cycle)return null;
    const saved=cycle.data?.finalMenu;
    if(saved&&Object.keys(saved).length>0)return saved;
    /* Fallback: build from current proposals */
    const fm={};
    if(isWeeklyMode){
      MEAL_TYPES.forEach(mt=>{
        const prop=getMealProposal(weekDays[0],mt);
        if(!prop)return;
        weekDays.forEach(d=>{if(!fm[fmt(d)])fm[fmt(d)]={};fm[fmt(d)][mt]=prop;});
      });
    }else{
      weekDays.forEach(d=>{
        MEAL_TYPES.forEach(mt=>{
          const prop=getMealProposal(d,mt);
          if(!prop)return;
          if(!fm[fmt(d)])fm[fmt(d)]={};
          fm[fmt(d)][mt]=prop;
        });
      });
    }
    return Object.keys(fm).length?fm:null;
  })();
  const getFinalMeal=(day,mealType)=>{if(!isFinalized)return null;return effectiveFinalMenu?.[fmt(day)]?.[mealType]||null;};
  /* Legacy compat: getFinalMealLegacy returns { name, nameAr, tags } for old grid rendering */
  const getFinalMealLegacy=(day,mealType)=>{
    const meal=getFinalMeal(day,mealType);
    if(!meal)return null;
    if(meal.courses){
      const main=meal.courses.find(c=>c.label==="Main")||meal.courses[0];
      if(main?.options?.[0])return{name:main.options[0].name,nameAr:main.options[0].nameAr||"",tags:main.options[0].tags||[]};
    }
    return meal.name?meal:null;
  };

  // ── Handlers ──────────────────────────────────────────────
  /* Effective deadline: workspace override > platform default */
  const effectiveDeadlineDay=menuDeadlineOverride?.day??platformMenuCfg.votingDeadlineDay??6;
  const effectiveDeadlineTime=menuDeadlineOverride?.time||platformMenuCfg.votingDeadlineTime||"23:59";
  /* Voting is for the UPCOMING week — deadline is the configured day of the week BEFORE it starts.
     Formula: weekStart (Monday) − 7 + deadlineDay (0=Mon … 5=Sat … 6=Sun)
     e.g. effectiveDeadlineDay=5 (Sat) → weekStart − 2 = the Saturday before the Monday */
  const computedDeadline=(()=>{
    const d=new Date(weekDays[0]);
    d.setDate(weekDays[0].getDate()-7+effectiveDeadlineDay);
    const[dlH,dlM]=effectiveDeadlineTime.split(":").map(Number);
    d.setHours(dlH,dlM,59,999);
    return d;
  })();
  /* If cycle already has a saved deadline use it, otherwise show the computed one */
  const votingDeadline=cycle?.votingDeadline?new Date(cycle.votingDeadline):computedDeadline;
  const deadlinePassed=votingDeadline<now;

  /* First week that voting is currently open for — the week residents should be voting on right now.
     Walks forward from the current week until we find one whose voting deadline is in the future. */
  const upcomingVotingWeekStart=(()=>{
    for(let o=0;o<=52;o++){
      const ws=getWeekStart(o);
      const dl=new Date(ws);
      dl.setDate(ws.getDate()-7+effectiveDeadlineDay);
      const[h,m]=effectiveDeadlineTime.split(":").map(Number);
      dl.setHours(h,m,59,999);
      if(dl>now)return fmt(ws);
    }
    return fmt(getWeekStart(1)); /* fallback */
  })();

  /* ensureCycle(wsOverride?)
     Creates (or returns existing) cycle for any given week start.
     When called without args it targets the currently displayed week. */
  const ensureCycle=(wsOverride)=>{
    const ws=wsOverride||fmt(weekDays[0]);
    const existing=(menuCycles||[]).find(c=>c.weekStart===ws&&!c.deletedAt);
    if(existing)return existing;
    /* Compute deadline and weekEnd for the target week */
    const wsDate=new Date(ws+"T12:00:00Z");
    const dlDate=new Date(wsDate);
    dlDate.setDate(wsDate.getDate()-7+effectiveDeadlineDay);
    const[dlH,dlM]=effectiveDeadlineTime.split(":").map(Number);
    dlDate.setHours(dlH,dlM,59,999);
    const weDate=new Date(wsDate);
    weDate.setDate(wsDate.getDate()+6);
    const c={id:`mc${Date.now()}`,wsKey,weekStart:ws,weekEnd:fmt(weDate),votingDeadline:dlDate.toISOString(),status:"voting",data:{votingMode:effectiveVotingMode}};
    dispatch({type:"ADD_MENU_CYCLE",cycle:c});
    /* Phase 6.1: fan out a "Vote on Weekly Menu" task to each
       eligible voter. Only runs on cycle creation — idempotent per
       the risk-register mitigation. Eligible = users whose role is
       in effectivePerms.vote, active, and not already voted. */
    fanOutMenuVoteTasks(c);
    return c;
  };

  /* Phase 6.1 helpers ─────────────────────────────────────── */
  const fanOutMenuVoteTasks=(cycle)=>{
    const menuTax=getEffectiveTaxonomy(state,platform,"Menu");
    const voteEntry=menuTax.find(e=>e.value==="vote_weekly")||null;
    const voteRoles=effectivePerms.vote||[];
    const voters=(users||[]).filter(u=>u.active!==false&&voteRoles.includes(u.role));
    if(!voters.length)return;
    const cycleLabel=cycle.weekStart||"";
    for(const v of voters){
      authFetch("/api/tasks?action=create_task",{method:"POST",headers:{"Content-Type":"application/json"},
        body:JSON.stringify({ws_key:wsKey,actor_id:currentUser?.id||"system",actor_name:currentUser?.name||"Menu",
          task:{title:`Vote on menu — ${cycleLabel}`,title_ar:`تصويت على القائمة — ${cycleLabel}`,
            priority:"medium",due_date:cycle.votingDeadline?cycle.votingDeadline.slice(0,10):null,
            created_by:currentUser?.id||"system",
            assigned_to:v.id,assigned_to_name:v.name||"",
            source_module:"Menu",task_type:"vote_weekly",
            task_type_id:voteEntry?voteEntry.id:null,
            context_payload:{cycleId:cycle.id,voterId:v.id,taxonomyId:voteEntry?voteEntry.id:null},
            linked_entities:[{type:"menu_cycle",id:cycle.id}],
            requires_approval:false,requires_expense:false}})})
        .then(r=>r.json()).then(d=>{if(d?.ok&&d.task)dispatch({type:"ADD_TASK",task:d.task});})
        .catch(e=>console.error("[Menu] vote task fan-out failed",e));
    }
  };

  const completeMenuVoteTask=(cycleId,voterId)=>{
    /* Find the voter's open task for this cycle and transition it
       to completed. No-op if no task (e.g. vote cast before Phase 6
       rolled out, or user is a legacy voter without a task). */
    const tk=(state.tasks||[]).find(t=>t.sourceModule==="Menu"
      &&(t.contextPayload||{}).cycleId===cycleId
      &&(t.contextPayload||{}).voterId===voterId
      &&t.status!=="completed"
      &&t.status!=="cancelled");
    if(!tk)return;
    authFetch("/api/tasks?action=transition",{method:"POST",headers:{"Content-Type":"application/json"},
      body:JSON.stringify({ws_key:wsKey,task_id:tk.id,new_status:"completed",
        actor_id:currentUser?.id||"",actor_name:currentUser?.name||""})})
      .then(r=>r.json()).then(d=>{if(d?.ok&&d.patch)dispatch({type:"UPDATE_TASK",id:tk.id,patch:d.patch});})
      .catch(e=>console.error("[Menu] vote task completion failed",e));
  };

  /* ── Proposition voting (approve / flag) ──────────────────
     Immediate: no two-phase draft. Clicking approve/flag writes to DB. */
  const castPropositionVote=(day,mealType,vote)=>{
    if(isFinalized||deadlinePassed||!canDo("vote"))return;
    const activeCycle=ensureCycle();
    const dayStr=isWeeklyMode?fmt(weekDays[0]):fmt(day);
    dispatch({type:"CAST_MENU_VOTE",vote:{
      id:`mv${Date.now()}`,wsKey,cycleId:activeCycle.id,
      day:dayStr,mealType,dishOptionId:"proposition",
      dishName:vote,userId:currentUser?.id
    }});
    completeMenuVoteTask(activeCycle.id,currentUser?.id);
  };

  /* ── Client-side finalize ────────────────────────────────
     Course model: finalMenu stores the proposal's course structure per slot.
     For weekly mode, replicates the same proposal across all 7 days. */
  /* Rank options in a course by total score descending */
  const rankCourseOptions=(day,mt,course,ci)=>{
    const scores=getOptionTotalScores(day,mt,ci,(course.options||[]).length);
    return(course.options||[])
      .map((opt,oi)=>({opt,score:scores[oi]||0,oi}))
      .sort((a,b)=>b.score-a.score);
  };
  /* Daily finalize: pick highest-scoring option per course per day */
  const buildFinalSlot=(day,mt)=>{
    const prop=getMealProposal(day,mt);
    if(!prop?.courses)return prop;
    const finalCourses=prop.courses.map((course,ci)=>{
      const ranked=rankCourseOptions(day,mt,course,ci);
      return{label:course.label,options:[ranked[0]?.opt||course.options[0]]};
    });
    return{courses:finalCourses};
  };
  const doClientFinalize=(activeCycle)=>{
    const finalMenu={};
    const weekly=isWeeklyMode;
    if(weekly){
      /* Weekly: distribute ranked options across 7 days.
         Day 1 → rank-1, Day 2 → rank-2, etc.
         Repeat top options once all "good" options (score > 2) are used,
         or cycle through all if none scored > 2 (or fewer options than days). */
      MEAL_TYPES.forEach(mt=>{
        const prop=getMealProposal(weekDays[0],mt);
        if(!prop?.courses)return;
        /* Pre-compute ranked options per course */
        const rankedByCourse=prop.courses.map((course,ci)=>rankCourseOptions(weekDays[0],mt,course,ci));
        weekDays.forEach((d,dayIdx)=>{
          if(!finalMenu[fmt(d)])finalMenu[fmt(d)]={};
          const finalCourses=prop.courses.map((course,ci)=>{
            const ranked=rankedByCourse[ci];
            if(!ranked.length)return{label:course.label,options:[]};
            const good=ranked.filter(r=>r.score>2);
            let chosen;
            if(good.length===0){
              /* All options scored ≤ 2 — no penalty for repeating, cycle through all */
              chosen=ranked[dayIdx%ranked.length];
            }else if(dayIdx<good.length){
              /* Still have a unique good option for this day */
              chosen=good[dayIdx];
            }else{
              /* Ran out of distinct good options — cycle through good pool */
              chosen=good[dayIdx%good.length];
            }
            return{label:course.label,options:[chosen.opt]};
          });
          finalMenu[fmt(d)][mt]={courses:finalCourses};
        });
      });
    }else{
      weekDays.forEach(d=>{
        MEAL_TYPES.forEach(mt=>{
          const finalMeal=buildFinalSlot(d,mt);
          if(!finalMeal)return;
          if(!finalMenu[fmt(d)])finalMenu[fmt(d)]={};
          finalMenu[fmt(d)][mt]=finalMeal;
        });
      });
    }
    dispatch({type:"UPDATE_MENU_CYCLE",id:activeCycle.id,patch:{status:"finalized",data:{...(activeCycle.data||{}),finalMenu}}});
    return finalMenu;
  };
  const doClientFinalizeWeekly=doClientFinalize; /* alias — same logic now handles both modes */

  // ── Auto-finalize at deadline ─────────────────────────────
  useEffect(()=>{
    if(!cycle||isFinalized||!deadlinePassed)return;
    const finalizer=cycle?.data?.votingMode==="weekly"?doClientFinalizeWeekly:doClientFinalize;
    finalizer(cycle);
    authFetch("/api/menu-finalize",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:wsKey,cycle_id:cycle.id})}).catch(()=>{});
  },[cycle?.id,deadlinePassed]);// eslint-disable-line

  /* ── AI Generate: course-structured proposals ────────────── */
  const handleGenerate=async()=>{
    if(genLoading)return;
    setGenLoading(true);setGenError(null);
    try{
      const anthropicKey=platform?.platformApiKeys?.anthropic||"";
      /* Effective options per course: custom field (capped 1–7) > preset button */
      const customVal=parseInt(genOptionsCustom);
      const effectiveOpts=genOptionsCustom.trim()&&customVal>=1&&customVal<=7?customVal:genOptionsPerCourse;
      /* Build courses config for each selected meal type */
      const coursesConfig={};
      genMealTypes.forEach(mt=>{coursesConfig[mt]=getEffectiveCourses(mt);});
      const dietaryList=(menuDietaryRequirements||[]).map(r=>r.type);
      let resp,data;
      try{
        resp=await authFetch("/api/menu-generate",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
          ws_key:wsKey,meal_types:genMealTypes,courses_config:coursesConfig,
          options_per_course:effectiveOpts,residents,
          preferences:genPrefs,dietary:dietaryList,anthropic_key:anthropicKey
        })});
        data=await resp.json();
      }catch(fetchErr){
        throw new Error(`Network error: ${fetchErr.message}. Check your internet connection and try again.`);
      }
      if(!resp.ok)throw new Error(data.error||`Server error (${resp.status})`);
      const proposals=data.proposals||[];
      if(proposals.length===0)throw new Error("AI returned no meal proposals. Try again.");
      /* Target week for "upcoming" scope = first week voting is currently open for */
      const upcomingWeekStart=upcomingVotingWeekStart;
      const targetWeekStart=genScope==="permanent"?null:upcomingWeekStart;
      /* Ensure a voting cycle exists for the target week */
      if(targetWeekStart)ensureCycle(targetWeekStart);
      else ensureCycle();
      const genNow=Date.now();
      proposals.forEach((p,i)=>{
        const mt=p.mealType||genMealTypes[i]||"lunch";
        const label=mt.charAt(0).toUpperCase()+mt.slice(1);
        dispatch({type:"ADD_MEAL",meal:{
          id:`ml_ai_${genNow+i}`,wsKey,
          name:`AI ${label}`,
          category:mt,
          scope:genScope==="permanent"?"permanent":"week",
          weekStart:genScope==="permanent"?undefined:upcomingWeekStart,
          data:{courses:p.courses||[]},
          source:"ai",
          createdBy:currentUser?.id
        }});
      });
      setShowGenerate(false);
    }catch(err){setGenError(err.message);}
    finally{setGenLoading(false);}
  };

  const handleFinalize=()=>{
    if(!cycle||isFinalized)return;
    const finalizer=cycle?.data?.votingMode==="weekly"?doClientFinalizeWeekly:doClientFinalize;
    finalizer(cycle);
    authFetch("/api/menu-finalize",{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({ws_key:wsKey,cycle_id:cycle.id})}).catch(()=>{});
  };

  /* ── Add Meal Proposal (manual entry with course structure) ── */
  const [mealForm,setMealForm]=useState(()=>({mealType:"breakfast",scope:"upcoming",courses:[{label:"Main",options:[{name:"",nameAr:""}]}]}));
  const addMealProposal=()=>{
    const valid=mealForm.courses.some(c=>c.options.some(o=>o.name.trim()));
    if(!valid)return;
    /* Determine weekStart for scope-scoped meals */
    const weekSt=mealForm.scope==="upcoming"?upcomingVotingWeekStart:undefined;
    /* Ensure a cycle exists for the target week so the menu grid renders */
    if(weekSt)ensureCycle(weekSt);
    const courses=mealForm.courses.map(c=>({label:c.label,options:c.options.filter(o=>o.name.trim()).map(o=>({name:o.name.trim(),nameAr:o.nameAr||"",description:o.description||"",tags:[]}))})).filter(c=>c.options.length>0);
    const scopeFields=weekSt?{scope:"week",weekStart:weekSt}:{scope:"permanent"};
    dispatch({type:"ADD_MEAL",meal:{
      ...scopeFields,
      id:`ml_${Date.now()}`,wsKey,
      name:`${mealForm.mealType.charAt(0).toUpperCase()+mealForm.mealType.slice(1)} Proposal`,
      category:mealForm.mealType,
      data:{courses},
      source:"manual",
      createdBy:currentUser?.id
    }});
    setMealForm({mealType:"breakfast",scope:"upcoming",courses:[{label:"Main",options:[{name:"",nameAr:""}]}]});
    setShowAddMeal(false);
  };



  const saveDietaryReq=()=>{
    const allSelected=[...selectedDietary,...(customDietaryInput.trim()?[customDietaryInput.trim()]:[])];
    if(allSelected.length===0)return;
    const now=Date.now();
    const newItems=allSelected.filter(s=>!(menuDietaryRequirements||[]).some(r=>r.type===s)).map((s,i)=>({id:`dr${now+i}`,type:s,description:""}));
    if(newItems.length>0)dispatch({type:"SET_MENU_DIETARY_REQS",requirements:[...(menuDietaryRequirements||[]),...newItems]});
    setSelectedDietary([]);setCustomDietaryInput("");setShowDietaryForm(false);
  };
  const removeDietaryReq=(id)=>dispatch({type:"SET_MENU_DIETARY_REQS",requirements:(menuDietaryRequirements||[]).filter(r=>r.id!==id)});

  const filteredMeals=(meals||[]).filter(m=>{
    if(m.deletedAt)return false;
    if(mealCatFilter!=="all"&&m.category!==mealCatFilter)return false;
    if(mealSearch&&!m.name.toLowerCase().includes(mealSearch.toLowerCase())&&!(m.nameAr||"").includes(mealSearch))return false;
    return true;
  });

  const deadlineLabel=votingDeadline.toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",hour:"2-digit",minute:"2-digit"});

  return(
    <div className="page">
      {/* ── AI Generate Modal (course-structured) ── */}
      {showGenerate&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>{setShowGenerate(false);setGenError(null);}}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div>
                <div className="stf-detail-title">✦ {t.aiMealGenerator}</div>
                <div className="stf-detail-sub" style={{textTransform:"none"}}>{t.menuGenerateDesc}</div>
              </div>
              <button className="stf-detail-close" onClick={()=>{setShowGenerate(false);setGenError(null);}}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="fg" style={{marginBottom:14}}>
                <label className="fl">{t.mealTypes} <span style={{fontWeight:400,color:"var(--g400)"}}>{isRTL?"(اختر واحداً أو أكثر)":"(select one or more)"}</span></label>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {MEAL_TYPES.map(mt=>(
                    <button key={mt} className={`btn btn-sm ${genMealTypes.includes(mt)?"btn-p":"btn-s"}`}
                      onClick={()=>setGenMealTypes(prev=>prev.includes(mt)?prev.length>1?prev.filter(x=>x!==mt):prev:[...prev,mt])}
                      style={{textTransform:"capitalize"}}>{mt}
                    </button>
                  ))}
                </div>
              </div>
              {/* Scope */}
              <div className="fg" style={{marginBottom:14}}>
                <label className="fl">{t.scope}</label>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                  {[
                    {v:"upcoming",l:t.upcomingWeek,d:`${t.weekOf} ${upcomingVotingWeekStart}`},
                    {v:"permanent",l:t.permanent,d:t.availableEveryWeek}
                  ].map(opt=>(
                    <button key={opt.v} type="button"
                      onClick={()=>setGenScope(opt.v)}
                      style={{
                        padding:"10px 12px",borderRadius:"var(--r2)",textAlign:"left",cursor:"pointer",
                        border:`2px solid ${genScope===opt.v?"var(--accent)":"var(--g200)"}`,
                        background:genScope===opt.v?"var(--gold-bg)":"var(--white)",
                        transition:"border-color .12s,background .12s"
                      }}>
                      <div style={{fontWeight:700,fontSize:12,color:genScope===opt.v?"var(--ink)":"var(--ink)"}}>{opt.l}</div>
                      <div style={{fontSize:11,color:genScope===opt.v?"var(--accent)":"var(--g400)",marginTop:2}}>{opt.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              {/* Options per course */}
              <div className="fg" style={{marginBottom:14}}>
                <label className="fl">{t.optionsPerCourse}</label>
                <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                  {[3,5].map(n=>(
                    <button key={n} type="button"
                      className={`btn btn-sm ${genOptionsPerCourse===n&&!genOptionsCustom.trim()?"btn-p":"btn-s"}`}
                      onClick={()=>{setGenOptionsPerCourse(n);setGenOptionsCustom("");}}>
                      {n} {isRTL?"خيارات":"options"}
                    </button>
                  ))}
                  <input
                    className="btn btn-sm btn-s" type="number" min={1} max={7}
                    value={genOptionsCustom}
                    onChange={e=>{
                      const v=e.target.value;
                      if(v===""||(/^\d+$/.test(v)&&parseInt(v)>=1&&parseInt(v)<=7)){setGenOptionsCustom(v);}
                    }}
                    placeholder={t.customRange}
                    style={{width:110,textAlign:"center",appearance:"textfield",MozAppearance:"textfield"}}/>
                </div>
                <div style={{fontSize:11,color:"var(--g400)",marginTop:4}}>
                  {(()=>{const cv=parseInt(genOptionsCustom);const eff=genOptionsCustom.trim()&&cv>=1&&cv<=7?cv:genOptionsPerCourse;return isRTL?`سيتم توليد ${eff} ${eff===1?"خيار":"خيارات"} لكل طبق`:`${eff} option${eff>1?"s":""} will be generated per course`;})()}
                </div>
              </div>
              {/* Course structure preview */}
              <div className="fg" style={{marginBottom:14}}>
                <label className="fl">{t.courseStructurePerMeal}</label>
                {[...genMealTypes].sort((a,b)=>["breakfast","lunch","dinner"].indexOf(a)-["breakfast","lunch","dinner"].indexOf(b)).map(mt=>(
                  <div key={mt} style={{padding:"6px 10px",background:"var(--g50)",border:"1px solid var(--g150)",borderRadius:"var(--r)",marginBottom:4,fontSize:12}}>
                    <strong style={{textTransform:"capitalize"}}>{mt}:</strong> {getEffectiveCourses(mt).join(" → ")}
                  </div>
                ))}
              </div>
              <div className="fg">
                <label className="fl">{t.preferencesLabel} <span style={{fontWeight:400,color:"var(--g400)"}}>({t.optional||"optional"})</span></label>
                <input className="fi" value={genPrefSearch} onChange={e=>setGenPrefSearch(e.target.value)} placeholder={t.filterPreferencesPh} style={{marginBottom:8}}/>
                <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                  {availablePreferences.filter(p=>!genPrefSearch||p.toLowerCase().includes(genPrefSearch.toLowerCase())).map(p=>(
                    <button key={p} className={`btn btn-xs ${genPrefs.includes(p)?"btn-p":"btn-s"}`}
                      onClick={()=>setGenPrefs(prev=>prev.includes(p)?prev.filter(x=>x!==p):[...prev,p])}>{p}
                    </button>
                  ))}
                </div>
                {genPrefs.length>0&&<div style={{marginTop:8,fontSize:11,color:"var(--g400)"}}>{t.selectedLabel}: {genPrefs.join(" · ")}&nbsp;<button className="btn btn-xs btn-g" onClick={()=>setGenPrefs([])}>{t.clear}</button></div>}
              </div>
              {genError&&<div className="notice" style={{marginTop:12,borderColor:"var(--red-bd)",background:"var(--red-bg)"}}><div className="notice-l" style={{color:"var(--red)"}}>{t.errorLabel}</div>{genError}</div>}
            </div>
            <div className="stf-detail-actions">
              <button className="btn btn-g" onClick={()=>{setShowGenerate(false);setGenError(null);}}>{t.cancel}</button>
              <button className="btn btn-p" disabled={genLoading} onClick={handleGenerate}>
                {genLoading?t.generating:t.generateProposalsBtn}
              </button>
            </div>
          </div>
        </div></Modal>
      )}
      {/* ── Add Meal to Library Modal (course structure + scope) ── */}
      {showAddMeal&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>setShowAddMeal(false)}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{t.addMealToLibrary}</div>
              <button className="stf-detail-close" onClick={()=>setShowAddMeal(false)}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div className="fr">
                <div className="fg" style={{flex:1}}>
                  <label className="fl">{t.mealType}</label>
                  <select className="fs" value={mealForm.mealType} onChange={e=>setMealForm(p=>({...p,mealType:e.target.value}))}>
                    {MEAL_TYPES.map(mt=><option key={mt} value={mt}>{mt.charAt(0).toUpperCase()+mt.slice(1)}</option>)}
                  </select>
                </div>
                <div className="fg" style={{flex:1}}>
                  <label className="fl">{t.saveToLibrary}</label>
                  <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
                    {[{v:"upcoming",l:t.upcomingWeek},{v:"permanent",l:t.permanent}].map(opt=>(
                      <button key={opt.v} type="button" className={`btn btn-sm ${mealForm.scope===opt.v?"btn-p":"btn-s"}`}
                        onClick={()=>setMealForm(p=>({...p,scope:opt.v}))}>{opt.l}</button>
                    ))}
                  </div>
                  <div style={{fontSize:11,color:"var(--g400)",marginTop:4}}>
                    {mealForm.scope==="upcoming"?(isRTL?`ظاهرة لأسبوع التصويت الحالي (${upcomingVotingWeekStart}).`:`Visible for the current voting week (${upcomingVotingWeekStart}).`):(isRTL?"دائماً ظاهرة في المكتبة لكل الأسابيع القادمة.":"Always visible in the library for all future weeks.")}
                  </div>
                </div>
              </div>
              <div style={{fontSize:11,color:"var(--g400)",marginBottom:10}}>
                {isRTL?`موصى به: ${optionsPerCourse} ${optionsPerCourse===1?"خيار":"خيارات"} لكل طبق (بناءً على ${residents} ساكن). أضف/احذف الأطباق والخيارات بحرية.`:`Recommended: ${optionsPerCourse} option${optionsPerCourse>1?"s":""} per course (based on ${residents} residents). Add/remove courses and options freely.`}
              </div>
              {mealForm.courses.map((course,ci)=>(
                <div key={ci} style={{marginBottom:14,padding:"10px 12px",border:"1px solid var(--g200)",borderRadius:"var(--r2)",background:"var(--g50)"}}>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8}}>
                    <input className="fi" style={{flex:1,fontWeight:600}} value={course.label} placeholder={t.courseLabelPh}
                      list={`cl-${mealForm.mealType}-${ci}`}
                      onChange={e=>{const c=[...mealForm.courses];c[ci]={...c[ci],label:e.target.value};setMealForm(p=>({...p,courses:c}));}}/>
                    <datalist id={`cl-${mealForm.mealType}-${ci}`}>
                      {getKnownCourseLabels(mealForm.mealType).map(l=><option key={l} value={l}/>)}
                    </datalist>
                    {mealForm.courses.length>1&&<button className="btn btn-g btn-xs" onClick={()=>setMealForm(p=>({...p,courses:p.courses.filter((_,i)=>i!==ci)}))}>{t.remove}</button>}
                  </div>
                  {course.options.map((opt,oi)=>(
                    <div key={oi} style={{display:"flex",gap:6,marginBottom:4,alignItems:"center"}}>
                      <input className="fi" style={{flex:2}} value={opt.name} placeholder={isRTL?`اسم الخيار ${oi+1}`:`Option ${oi+1} name`}
                        onChange={e=>{const c=[...mealForm.courses];const opts=[...c[ci].options];opts[oi]={...opts[oi],name:e.target.value};c[ci]={...c[ci],options:opts};setMealForm(p=>({...p,courses:c}));}}/>
                      <input className="fi" style={{flex:1,direction:"rtl"}} value={opt.nameAr||""} placeholder={t.arabicPh}
                        onChange={e=>{const c=[...mealForm.courses];const opts=[...c[ci].options];opts[oi]={...opts[oi],nameAr:e.target.value};c[ci]={...c[ci],options:opts};setMealForm(p=>({...p,courses:c}));}}/>
                      {course.options.length>1&&<button className="btn btn-g btn-xs" style={{flexShrink:0}}
                        onClick={()=>{const c=[...mealForm.courses];c[ci]={...c[ci],options:c[ci].options.filter((_,i)=>i!==oi)};setMealForm(p=>({...p,courses:c}));}}>×</button>}
                    </div>
                  ))}
                  <button className="btn btn-s btn-xs" style={{marginTop:4}}
                    onClick={()=>{const c=[...mealForm.courses];c[ci]={...c[ci],options:[...c[ci].options,{name:"",nameAr:""}]};setMealForm(p=>({...p,courses:c}));}}>+ {t.optionBtn}</button>
                </div>
              ))}
              <button className="btn btn-s btn-sm" onClick={()=>setMealForm(p=>({...p,courses:[...p.courses,{label:"",options:[{name:"",nameAr:""}]}]}))}>+ {t.addCourseBtn}</button>
            </div>
            <div className="stf-detail-actions">
              <button className="btn btn-g" onClick={()=>setShowAddMeal(false)}>{t.cancel}</button>
              <button className="btn btn-p" onClick={addMealProposal}>{t.saveToLibrary}</button>
            </div>
          </div>
        </div></Modal>
      )}
      {showDietaryForm&&(
        <Modal><div className="stf-detail-overlay" onClick={()=>{setShowDietaryForm(false);setSelectedDietary([]);setCustomDietaryInput("");}}>
          <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
            <div className="stf-detail-handle"/>
            <div className="stf-detail-header">
              <div className="stf-detail-title">{t.addDietaryRequirements}</div>
              <button className="stf-detail-close" onClick={()=>{setShowDietaryForm(false);setSelectedDietary([]);setCustomDietaryInput("");}}>✕</button>
            </div>
            <div className="stf-detail-body">
              <div style={{fontSize:12,color:"var(--g400)",marginBottom:12}}>{t.dietaryPickMsg}</div>
              <div style={{display:"flex",flexWrap:"wrap",gap:7}}>
                {DIETARY_SUGGESTIONS.map(s=>{
                  const on=selectedDietary.includes(s);
                  return(
                    <button key={s} className={`btn btn-sm ${on?"btn-p":"btn-s"}`}
                      onClick={()=>setSelectedDietary(p=>on?p.filter(x=>x!==s):[...p,s])}>
                      {s}
                    </button>
                  );
                })}
              </div>
              <div style={{marginTop:14}}>
                <label className="fl">{t.orTypeCustomRequirement}</label>
                <div style={{display:"flex",gap:8}}>
                  <input className="fi" value={customDietaryInput} onChange={e=>setCustomDietaryInput(e.target.value)}
                    placeholder={isRTL?"مثال: بدون مكسرات، سعرات منخفضة…":"e.g. No nuts, Low calorie…"}
                    onKeyDown={e=>{if(e.key==="Enter"&&customDietaryInput.trim()){saveDietaryReq();}}}
                    style={{flex:1}}/>
                </div>
              </div>
              {(selectedDietary.length>0||customDietaryInput.trim())&&(
                <div style={{marginTop:12,fontSize:12,color:"var(--g400)"}}>
                  {t.selectedLabel}: <strong style={{color:"var(--ink)"}}>{[...selectedDietary,...(customDietaryInput.trim()?[customDietaryInput.trim()]:[])].join(", ")}</strong>
                </div>
              )}
            </div>
            <div className="stf-detail-actions">
              <button className="btn btn-g" onClick={()=>{setShowDietaryForm(false);setSelectedDietary([]);setCustomDietaryInput("");}}>{t.cancel}</button>
              <button className="btn btn-p" disabled={selectedDietary.length===0&&!customDietaryInput.trim()} onClick={saveDietaryReq}>
                {t.add} {(selectedDietary.length+(customDietaryInput.trim()?1:0))>0?`(${selectedDietary.length+(customDietaryInput.trim()?1:0)})`:""}
              </button>
            </div>
          </div>
        </div></Modal>
      )}

      <div className="ph">
        <div>
          <div className="ptitle">{state.platformModuleNames?.Menu||t.menuPlanningTitle}</div>
          <div className="psub">{state.moduleSubtitles?.Menu||t.menuSubtitle}</div>
        </div>
        {/* Header buttons hidden on mobile — split FAB used instead */}
        {tab==="library"&&<div className="inc-tabs-desktop" style={{display:"flex",gap:8}}>
          {canDo("addDishes")&&<button className="btn btn-s btn-sm" onClick={()=>setShowAddMeal(true)}>+ {t.addMealBtn}</button>}
          {canDo("generateMeals")&&<button className="btn btn-p btn-sm" onClick={()=>setShowGenerate(true)}>✦ {t.aiGenerateBtn}</button>}
        </div>}
      </div>

      {(()=>{const menuTabs=[{k:"menu",l:t.weeklyMenuTab},{k:"library",l:t.mealLibraryTab},{k:"dietary",l:`${t.dietaryTab} (${(menuDietaryRequirements||[]).length})`}];return(<>
        <div className="inc-tabs-desktop" style={{display:"flex",gap:0,marginBottom:16}}>
          {menuTabs.map(tb=><div key={tb.k} className={`tab ${tab===tb.k?"active":""}`} style={{marginBottom:0}} onClick={()=>setTab(tb.k)}>{tb.l}</div>)}
        </div>
        <div className="inc-status-tabs" style={{marginBottom:16}}>
          {menuTabs.map(tb=>(
            <button key={tb.k} className={`inc-status-tab ${tab===tb.k?"active":""}`} onClick={()=>setTab(tb.k)}>{tb.l}</button>
          ))}
        </div>
      </>);})()}

      {tab==="menu"&&(
        <div style={{overflowX:"hidden",width:"100%",boxSizing:"border-box"}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14,flexWrap:"wrap",padding:"10px 14px",background:"rgba(255,255,255,.55)",backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",border:"1px solid rgba(255,255,255,.35)",borderRadius:16,boxShadow:"0 4px 24px rgba(0,0,0,.03), 0 0 0 0.5px rgba(255,255,255,.5) inset"}}>
            <button className="btn btn-g btn-sm" onClick={()=>setWeekOffset(o=>o-1)}>←</button>
            <div style={{flex:1,textAlign:"center",fontSize:13,fontWeight:600,minWidth:0}}>
              {isFinalized&&<span style={{color:"var(--ink)",marginInlineEnd:4}}>✓</span>}
              {weekDays[0].toLocaleDateString("en-GB",{day:"numeric",month:"short"})} — {weekDays[6].toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"})}
            </div>
            <button className="btn btn-g btn-sm" onClick={()=>setWeekOffset(o=>o+1)}>→</button>
            {weekOffset!==0&&<button className="btn btn-g btn-xs" onClick={()=>setWeekOffset(0)}>{t.thisWeek}</button>}
          </div>
          {cycle&&!isFinalized&&(
            <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10,padding:"10px 14px",background:"rgba(255,255,255,.55)",backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",border:"1px solid rgba(255,255,255,.35)",borderRadius:16,flexWrap:"wrap",boxShadow:"0 4px 24px rgba(0,0,0,.03), 0 0 0 0.5px rgba(255,255,255,.5) inset"}}>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap",flex:1,minWidth:0}}>
                <span style={{display:"inline-flex",alignItems:"center",gap:5,padding:"3px 10px",borderRadius:20,fontSize:11.5,fontWeight:700,letterSpacing:".02em",whiteSpace:"nowrap",
                  background:deadlinePassed?"var(--ylw-bg,#fffbeb)":"rgba(0,0,0,.04)",
                  color:deadlinePassed?"var(--ylw,#b45309)":"var(--ink)",
                  border:`1px solid ${deadlinePassed?"var(--ylw-bd,#fde68a)":"var(--g200)"}`}}>
                  {deadlinePassed?t.votingClosedPill:t.votingOpenPill}
                </span>
                <span style={{fontSize:11,color:"var(--g400)",minWidth:0,wordBreak:"break-word"}}>
                  {deadlinePassed?t.closedOn:t.closesOnLabel}: <strong style={{color:"var(--g600)"}}>{deadlineLabel}</strong>
                </span>
              </div>
              {canDo("finalize")&&!isFinalized&&<button className="btn btn-p btn-sm" style={{flexShrink:0}} onClick={handleFinalize}>{t.finalizeMenuBtn}</button>}
            </div>
          )}
          {/* ── Voting status hints ── */}
          {isVoting&&!deadlinePassed&&canDo("vote")&&(menuVotes||[]).some(v=>matchesCycle(v)&&v.dishOptionId?.startsWith("course_")&&v.userId===currentUser?.id&&!v.deletedAt)&&(
            <div style={{marginBottom:10,padding:"8px 14px",fontSize:11,color:"var(--g400)",background:"rgba(255,255,255,.4)",border:"1px solid rgba(255,255,255,.35)",borderRadius:12}}>
              {t.votesSavedHint}
            </div>
          )}
          {isPastWeek&&!isFinalized&&(
            <div style={{textAlign:"center",padding:"40px 20px"}}>
              <div style={{fontSize:36,marginBottom:10,opacity:.5}}>📋</div>
              <div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.noVotingRecorded}</div>
              <div style={{fontSize:13,color:"var(--g400)"}}>{t.noVotingRecordedDesc}</div>
            </div>
          )}
          {!isPastWeek&&!cycle&&!deadlinePassed&&canDo("vote")&&(
            <div style={{marginBottom:10,padding:"8px 14px",fontSize:12,color:"var(--g400)",background:"rgba(255,255,255,.4)",border:"1px solid rgba(255,255,255,.35)",borderRadius:12,lineHeight:1.5}}>
              {t.aiGenerateHint} <strong>{deadlineLabel}</strong>.
            </div>
          )}
          {!isPastWeek&&!cycle&&deadlinePassed&&(
            <div style={{textAlign:"center",padding:"40px 20px"}}>
              <div style={{fontSize:36,marginBottom:10,opacity:.5}}>⏰</div>
              <div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.votingWindowClosed}</div>
              <div style={{fontSize:13,color:"var(--g400)"}}>{t.closedOn} <strong>{deadlineLabel}</strong>. {t.votingWindowClosedDesc}</div>
            </div>
          )}
          {/* ── FINALIZED: 7-column summary grid (desktop) ── */}
          {isFinalized&&(<>
            <div className="menu-grid-desktop" style={{overflowX:"auto",WebkitOverflowScrolling:"touch"}}>
              <div style={{display:"grid",gridTemplateColumns:"80px repeat(7,minmax(90px,1fr))",minWidth:700,gap:1,background:"var(--g200)",borderRadius:"var(--r2)",overflow:"hidden"}}>
                <div style={{background:"var(--g50)",padding:"8px 10px"}}/>
                {weekDays.map((d,i)=>{const isToday=fmt(d)===fmt(now);return(
                  <div key={i} style={{background:isToday?"var(--ink)":"var(--g50)",padding:"8px 6px",textAlign:"center"}}>
                    <div style={{fontSize:10,fontWeight:600,color:isToday?"rgba(255,255,255,.6)":"var(--g400)",textTransform:"uppercase",letterSpacing:".07em"}}>{DAY_LABELS[i]}</div>
                    <div style={{fontSize:13,fontWeight:600,color:isToday?"white":"var(--ink)"}}>{d.getDate()}</div>
                  </div>
                );})}
                {MEAL_TYPES.map(mt=>[
                  <div key={`${mt}-lbl`} style={{background:"var(--white)",padding:"10px 6px",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <div style={{fontSize:9,fontWeight:700,color:"var(--g500)",textTransform:"uppercase",letterSpacing:".07em",writingMode:"vertical-rl"}}>{mt}</div>
                  </div>,
                  ...weekDays.map((d,di)=>{const fm=getFinalMeal(d,mt);return(
                    <div key={`${mt}-${di}`} style={{background:"var(--white)",padding:"4px",minHeight:60,display:"flex",flexDirection:"column",gap:2}}>
                      {fm?.courses?fm.courses.map((course,ci)=>(
                        <div key={ci}>
                          <div style={{fontSize:7,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".05em"}}>{course.label}</div>
                          {(course.options||[]).map((opt,oi)=>(
                            <div key={oi} style={{padding:"2px 4px",background:"var(--grn-bg)",border:"1px solid var(--grn-bd)",borderRadius:"var(--r)",fontSize:9,fontWeight:600,color:"var(--grn)",lineHeight:1.3,marginBottom:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                              {isRTL&&opt.nameAr?opt.nameAr:opt.name}
                            </div>
                          ))}
                        </div>
                      )):<div style={{fontSize:10,color:"var(--g300)",textAlign:"center",paddingTop:8}}>—</div>}
                    </div>
                  );}),
                ])}
              </div>
            </div>
            {/* Mobile: day-by-day cards for finalized menu */}
            {/* Mobile: swipeable day carousel for finalized menu */}
            <div className="menu-grid-mobile" style={{display:"none",flexDirection:"column",gap:0}}>
              {(()=>{
                const todayIdx=weekDays.findIndex(d=>fmt(d)===fmt(now));
                const initDay=todayIdx>=0?todayIdx:0;
                return <MenuCarousel weekDays={weekDays} MEAL_TYPES={MEAL_TYPES} DAY_LABELS={DAY_LABELS} getFinalMeal={getFinalMeal} isRTL={isRTL} now={now} fmt={fmt} initDay={initDay} t={t}/>;
              })()}
            </div>
          </>)}
          {/* ── Future week — no cycle and no permanent meals ── */}
          {!isFinalized&&!isPastWeek&&!cycle&&weekOffset>0&&!(meals||[]).some(m=>!m.deletedAt&&m.scope!=="week")&&(
            <div style={{textAlign:"center",padding:"40px 20px"}}>
              <div style={{fontSize:36,marginBottom:10,opacity:.5}}>🍽</div>
              <div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.noProposalsYet}</div>
              <div style={{fontSize:13,color:"var(--g400)",marginBottom:14}}>{t.noProposalsYetDesc}</div>
              {canDo("generateMeals")&&<button className="btn btn-p btn-sm" onClick={()=>setShowGenerate(true)}>✦ {t.aiGenerateBtn}</button>}
            </div>
          )}
          {/* ── VOTING: per-course option picker (show when cycle exists OR permanent meals available) ── */}
          {!isFinalized&&!isPastWeek&&(cycle||(meals||[]).some(m=>!m.deletedAt&&m.scope!=="week"))&&(()=>{
            const voteDay=isWeeklyMode?weekDays[0]:weekDays[selectedDay];
            const proposal=getMealProposal(voteDay,votingMealTab);
            return(
              <div>
                {/* Daily mode: day-selector tabs */}
                {!isWeeklyMode&&(
                  <div style={{display:"flex",gap:6,overflowX:"auto",WebkitOverflowScrolling:"touch",paddingBottom:4,marginBottom:14,scrollbarWidth:"none"}}>
                    {weekDays.map((d,i)=>{
                      const isToday=fmt(d)===fmt(now);
                      const isSel=selectedDay===i;
                      const hasVoted=(menuVotes||[]).some(v=>matchesCycle(v)&&v.day===fmt(d)&&v.dishOptionId?.startsWith("course_")&&v.userId===currentUser?.id&&!v.deletedAt);
                      return(
                        <button key={i} onClick={()=>setSelectedDay(i)}
                          style={{flexShrink:0,minWidth:52,padding:"10px 8px",borderRadius:14,
                            border:isSel?"none":"1px solid rgba(255,255,255,.35)",
                            background:isSel?"var(--ink)":isToday?"rgba(255,255,255,.65)":"rgba(255,255,255,.4)",
                            backdropFilter:isSel?"none":"blur(20px) saturate(180%)",WebkitBackdropFilter:isSel?"none":"blur(20px) saturate(180%)",
                            boxShadow:isSel?"0 4px 16px rgba(0,0,0,.12)":"0 1px 4px rgba(0,0,0,.03)",
                            cursor:"pointer",textAlign:"center",transition:"all .2s"}}>
                          <div style={{fontSize:9,fontWeight:700,textTransform:"uppercase",letterSpacing:".07em",color:isSel?"rgba(255,255,255,.7)":"var(--g400)"}}>{DAY_LABELS[i]}</div>
                          <div style={{fontSize:15,fontWeight:700,color:isSel?"white":"var(--ink)"}}>{d.getDate()}</div>
                          {hasVoted&&!isSel&&<div style={{width:5,height:5,borderRadius:"50%",background:"var(--ink)",margin:"3px auto 0"}}/>}
                          {isToday&&!isSel&&<div style={{width:4,height:4,borderRadius:2,background:"var(--ink)",margin:"2px auto 0",opacity:.4}}/>}
                        </button>
                      );
                    })}
                  </div>
                )}
                {isWeeklyMode&&<div style={{fontSize:11,color:"var(--g400)",marginBottom:12,padding:"8px 14px",background:"rgba(255,255,255,.4)",border:"1px solid rgba(255,255,255,.35)",borderRadius:12}}>{t.weeklyVoteHint}</div>}
                {/* Meal type tabs */}
                <div style={{display:"flex",gap:4,marginBottom:20,padding:3,background:"rgba(255,255,255,.45)",backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",border:"1px solid rgba(255,255,255,.35)",borderRadius:14,boxShadow:"0 4px 24px rgba(0,0,0,.03), 0 0 0 0.5px rgba(255,255,255,.5) inset"}}>
                  {MEAL_TYPES.map(mt=>(
                    <button key={mt} onClick={()=>setVotingMealTab(mt)}
                      style={{flex:1,padding:"9px 4px",border:"none",borderRadius:11,
                        background:votingMealTab===mt?"var(--white)":"transparent",
                        boxShadow:votingMealTab===mt?"0 1px 4px rgba(0,0,0,.06)":"none",
                        cursor:"pointer",
                        fontSize:12,fontWeight:700,textTransform:"uppercase",letterSpacing:".06em",
                        color:votingMealTab===mt?"var(--ink)":"var(--g400)",transition:"all .2s"}}>
                      {mt}
                    </button>
                  ))}
                </div>
                {/* Course option 1–5 rating */}
                {!proposal?.courses?(
                  <div style={{textAlign:"center",padding:"40px 20px"}}>
                    <div style={{fontSize:36,marginBottom:10,opacity:.5}}>🍽</div>
                    <div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.noProposalFor} {votingMealTab} {t.yetLabel}</div>
                    <div style={{fontSize:13,color:"var(--g400)",marginBottom:canDo("generateMeals")?14:0}}>{t.generateProposalsToStart}</div>
                    {canDo("generateMeals")&&<button className="btn btn-p btn-sm" onClick={()=>setShowGenerate(true)}>✦ {t.aiGenerateBtn}</button>}
                  </div>
                ):(
                  <div style={{display:"flex",flexDirection:"column",gap:16}}>
                    {proposal.courses.map((course,ci)=>{
                      const scores=getOptionTotalScores(voteDay,votingMealTab,ci,(course.options||[]).length);
                      const maxScore=Math.max(1,...Object.values(scores));
                      const canVote=canDo("vote")&&!deadlinePassed;
                      return(
                        <div key={ci}>
                          <div style={{fontSize:10,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:8,paddingInlineStart:2}}>{course.label}</div>
                          <div style={{background:"rgba(255,255,255,.55)",backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",border:"1px solid rgba(255,255,255,.35)",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,.03), 0 0 0 0.5px rgba(255,255,255,.5) inset",padding:"6px"}}>
                          {canVote&&<div style={{fontSize:11,color:"var(--g400)",padding:"4px 8px 6px"}}>{t.rateEachHint}</div>}
                          <div style={{display:"flex",flexDirection:"column",gap:6}}>
                            {(course.options||[]).map((opt,oi)=>{
                              const myRating=getUserOptionRating(voteDay,votingMealTab,ci,oi);
                              const total=scores[oi]||0;
                              const barPct=maxScore>0?Math.round(total/maxScore*100):0;
                              const hasRated=myRating>0;
                              return(
                                <div key={oi} style={{padding:"12px 14px",borderRadius:12,
                                  border:hasRated?"1px solid var(--gold-bd)":"1px solid rgba(0,0,0,.04)",
                                  background:hasRated?"var(--gold-bg)":"rgba(255,255,255,.6)",
                                  boxShadow:hasRated?`0 2px 12px rgba(var(--accent-rgb),.1)`:"none"}}>
                                  <div style={{display:"flex",alignItems:"flex-start",gap:8,marginBottom:6}}>
                                    <div style={{flex:1,minWidth:0}}>
                                      <div style={{fontWeight:600,fontSize:14,color:"var(--ink)"}}>
                                        {isRTL&&opt.nameAr?opt.nameAr:opt.name}
                                      </div>
                                      {opt.description&&<div style={{fontSize:12,color:"var(--g400)",marginTop:2,lineHeight:1.4}}>{opt.description}</div>}
                                    </div>
                                    {total>0&&(
                                      <div style={{flexShrink:0,textAlign:"right"}}>
                                        <div style={{fontSize:13,fontWeight:700,color:"var(--g600)"}}>{total}</div>
                                        <div style={{width:48,height:4,borderRadius:2,background:"var(--g150)",marginTop:3,overflow:"hidden"}}>
                                          <div style={{width:`${barPct}%`,height:"100%",background:"var(--accent)",borderRadius:2,transition:"width .3s"}}/>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {canVote&&(
                                    <div style={{display:"flex",gap:4,alignItems:"center"}}>
                                      <span style={{fontSize:11,color:hasRated?"var(--accent)":"var(--g400)",marginInlineEnd:2,fontWeight:hasRated?600:400}}>
                                        {hasRated?`${myRating}/5`:t.rateLabel}
                                      </span>
                                      {[1,2,3,4,5].map(n=>(
                                        <button key={n} onClick={()=>castOptionRating(voteDay,votingMealTab,ci,oi,n)}
                                          style={{width:36,height:36,borderRadius:10,flexShrink:0,
                                            border:myRating>=n?"none":"1px solid var(--g200)",
                                            background:myRating>=n?"var(--primary,var(--ink))":"rgba(255,255,255,.6)",
                                            backdropFilter:myRating>=n?"none":"blur(10px)",WebkitBackdropFilter:myRating>=n?"none":"blur(10px)",
                                            color:myRating>=n?"white":"var(--g400)",
                                            cursor:"pointer",fontSize:14,fontWeight:700,transition:"all .15s",
                                            boxShadow:myRating>=n?`0 2px 8px rgba(var(--accent-rgb),.25)`:"none"}}>
                                          {n}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          </div>
                        </div>
                      );
                    })}
                    {deadlinePassed&&<div style={{fontSize:11,color:"var(--g400)",textAlign:"center",padding:"10px 14px",background:"rgba(255,255,255,.4)",border:"1px solid rgba(255,255,255,.35)",borderRadius:12}}>{t.votingClosedFinalize}</div>}
                    {canDo("generateMeals")&&!deadlinePassed&&<div style={{marginTop:4,fontSize:11,color:"var(--g400)",padding:"6px 10px"}}><button className="btn btn-s btn-xs" style={{fontSize:11}} onClick={()=>setShowGenerate(true)}>✦ {t.regenerateBtn}</button> {t.regenerateHint}</div>}
                  </div>
                )}
              </div>
            );
          })()}
          <div style={{height:100}}/>
        </div>
      )}

      {tab==="library"&&(
        <div>
          {/* Category filter pills + search — glass container */}
          <div className="rep-period-card" style={{marginBottom:16,padding:"10px 12px"}}>
            <div style={{marginBottom:8}}>
              <input className="fi" style={{width:"100%",boxSizing:"border-box",background:"rgba(255,255,255,.6)",border:"1px solid rgba(0,0,0,.04)",borderRadius:10,fontSize:13,padding:"9px 12px"}} value={mealSearch} onChange={e=>setMealSearch(e.target.value)} placeholder={isRTL?"بحث الوجبات…":"Search meals…"}/>
            </div>
            <div style={{display:"flex",gap:4}}>
              <button className={`btn btn-sm ${mealCatFilter==="all"?"btn-p":"btn-s"}`} style={{flex:1,borderRadius:11,fontSize:12,padding:"8px 4px",fontWeight:mealCatFilter==="all"?600:500,justifyContent:"center",textAlign:"center"}} onClick={()=>setMealCatFilter("all")}>{isRTL?"الكل":"All"}</button>
              {(mealCategories||[]).filter(c=>c!=="snack").map(c=>(
                <button key={c} className={`btn btn-sm ${mealCatFilter===c?"btn-p":"btn-s"}`} style={{flex:1,borderRadius:11,fontSize:12,padding:"8px 4px",textTransform:"capitalize",fontWeight:mealCatFilter===c?600:500,justifyContent:"center",textAlign:"center"}} onClick={()=>setMealCatFilter(c)}>{c}</button>
              ))}
            </div>
          </div>
          {/* Empty state */}
          {filteredMeals.length===0&&(
            <div style={{textAlign:"center",padding:"40px 20px"}}>
              <div style={{fontSize:36,marginBottom:10,opacity:.5}}>🍽</div>
              <div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.noMealsYet}</div>
              <div style={{fontSize:13,color:"var(--g400)"}}>{t.addMealOrUseAi}</div>
            </div>
          )}
          {/* Meals grouped by category — glass cards */}
          {(mealCategories||[]).map(cat=>{
            const catMeals=filteredMeals.filter(m=>m.category===cat);
            if(!catMeals.length)return null;
            return(
              <div key={cat} style={{marginBottom:16}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--g400)",textTransform:"uppercase",letterSpacing:".1em",marginBottom:8,paddingInlineStart:2}}>{cat}</div>
                <div style={{background:"rgba(255,255,255,.55)",backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",border:"1px solid rgba(255,255,255,.35)",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,.03), 0 0 0 0.5px rgba(255,255,255,.5) inset"}}>
                  {catMeals.map((meal,mi)=>{
                    const hasCourses=meal.data?.courses?.length>0;
                    const isConfirming=confirmDeleteMeal===meal.id;
                    return(
                      <div key={meal.id} style={{padding:"12px 16px",borderBottom:mi<catMeals.length-1?"1px solid rgba(0,0,0,.04)":"none"}}>
                        <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{fontSize:14,fontWeight:600,color:"var(--ink)",marginBottom:hasCourses?4:0}}>
                              {isRTL&&meal.nameAr?meal.nameAr:meal.name}
                              {meal.source==="ai"&&<span style={{marginInlineStart:6,fontSize:8,padding:"1px 5px",borderRadius:6,background:"rgba(0,0,0,.04)",color:"var(--g400)",verticalAlign:"middle"}}>AI</span>}
                              {meal.scope==="week"&&<span style={{marginInlineStart:4,fontSize:8,padding:"1px 5px",borderRadius:6,background:"var(--ylw-bg,#fffbeb)",color:"var(--ylw,#b45309)",border:"1px solid var(--ylw-bd,#fde68a)",verticalAlign:"middle"}}>{meal.weekStart||"Week"}</span>}
                            </div>
                            {hasCourses&&(
                              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                                {meal.data.courses.map((course,ci)=>(
                                  <div key={ci} style={{fontSize:12,color:"var(--g400)",lineHeight:1.4}}>
                                    <span style={{fontWeight:600,color:"var(--g500)",fontSize:10,textTransform:"uppercase",letterSpacing:".04em"}}>{course.label}: </span>
                                    {(course.options||[]).map(o=>isRTL&&o.nameAr?o.nameAr:o.name).filter(Boolean).join(" · ")}
                                  </div>
                                ))}
                              </div>
                            )}
                            {!hasCourses&&meal.nameAr&&<div style={{fontSize:11,color:"var(--g400)",direction:"rtl"}}>{isRTL?meal.name:meal.nameAr}</div>}
                          </div>
                          {canDo("addDishes")&&!isConfirming&&<button style={{width:28,height:28,borderRadius:8,background:"rgba(0,0,0,.03)",border:"none",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,color:"var(--g400)",fontSize:12}} onClick={()=>setConfirmDeleteMeal(meal.id)}>✕</button>}
                        </div>
                        {isConfirming&&(
                          <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8,padding:"8px 10px",background:"rgba(0,0,0,.02)",borderRadius:10}}>
                            <span style={{fontSize:12,color:"var(--g500)",flex:1}}>{isRTL?"حذف هذه الوجبة؟":"Delete this meal?"}</span>
                            <button className="btn btn-d btn-xs" style={{fontSize:11}} onClick={()=>{dispatch({type:"REMOVE_MEAL",id:meal.id});setConfirmDeleteMeal(null);}}>{t.deleteBtn}</button>
                            <button className="btn btn-g btn-xs" style={{fontSize:11}} onClick={()=>setConfirmDeleteMeal(null)}>{t.cancelBtn}</button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div style={{height:100}}/>
        </div>
      )}

      {tab==="dietary"&&(
        <div>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:12,color:"var(--g400)",lineHeight:1.5}}>{isRTL?"تُطبَّق عند توليد الوجبات وتُدرَج في إشعارات الموظفين المعنيين.":"Applied when generating meals and included in relevant staff notifications."}</div>
          </div>
          {(menuDietaryRequirements||[]).length===0&&(
            <div style={{textAlign:"center",padding:"40px 20px"}}>
              <div style={{fontSize:36,marginBottom:10,opacity:.5}}>🥗</div>
              <div style={{fontWeight:600,color:"var(--ink)",marginBottom:4}}>{t.noDietaryRequirements}</div>
              <div style={{fontSize:13,color:"var(--g400)"}}>{isRTL?"أضف متطلبات الحمية والحساسية":"Add diet & allergy requirements"}</div>
            </div>
          )}
          {(menuDietaryRequirements||[]).length>0&&(
            <div style={{background:"rgba(255,255,255,.55)",backdropFilter:"blur(40px) saturate(200%)",WebkitBackdropFilter:"blur(40px) saturate(200%)",border:"1px solid rgba(255,255,255,.35)",borderRadius:16,overflow:"hidden",boxShadow:"0 4px 24px rgba(0,0,0,.03), 0 0 0 0.5px rgba(255,255,255,.5) inset"}}>
              {(menuDietaryRequirements||[]).map((req,ri)=>{
                const isConfirming=confirmDeleteDietary===req.id;
                return(
                <div key={req.id} style={{padding:"12px 16px",borderBottom:ri<(menuDietaryRequirements||[]).length-1?"1px solid rgba(0,0,0,.04)":"none"}}>
                  <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:"var(--ink)"}}>{req.type}</div>
                      {req.description&&<div style={{fontSize:12,color:"var(--g400)",marginTop:2}}>{req.description}</div>}
                    </div>
                    {canDo("manageDietary")&&!isConfirming&&<button style={{width:28,height:28,borderRadius:8,background:"rgba(0,0,0,.03)",border:"none",display:"flex",alignItems:"center",justifyContent:"center",cursor:"pointer",flexShrink:0,color:"var(--g400)",fontSize:12}} onClick={()=>setConfirmDeleteDietary(req.id)}>✕</button>}
                  </div>
                  {isConfirming&&(
                    <div style={{display:"flex",gap:8,alignItems:"center",marginTop:8,padding:"8px 10px",background:"rgba(0,0,0,.02)",borderRadius:10}}>
                      <span style={{fontSize:12,color:"var(--g500)",flex:1}}>{isRTL?"حذف هذا المتطلب؟":"Delete this requirement?"}</span>
                      <button className="btn btn-d btn-xs" style={{fontSize:11}} onClick={()=>{removeDietaryReq(req.id);setConfirmDeleteDietary(null);}}>{t.deleteBtn}</button>
                      <button className="btn btn-g btn-xs" style={{fontSize:11}} onClick={()=>setConfirmDeleteDietary(null)}>{t.cancelBtn}</button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}
          <div style={{height:100}}/>
        </div>
      )}

    </div>
  );
}


export {
  CUISINE_DB,
  DEFAULT_MENU_PREFERENCES,
  DIETARY_SUGGESTIONS,
  DEFAULT_COURSE_SCALING,
  DEFAULT_COURSES_PER_MEAL,
  MenuCarousel,
};

export default MenuModule;
